// Lo que el motor no guarda del correo, en objetos propios de la app: estado por persona y hilo (leido hasta,
// destacado, archivado), buzones de grupo con sus miembros y plantillas (textos rapidos y firmas).
import { create, f, listAll, remove, update } from './rest';

export type EstadoCorreo = { id: string; hiloId: string; leidoEn: string | null; destacado: boolean; archivado: boolean };
type RawEstado = { id: string; hiloId: string; miembroId: string; leidoEn: string | null; destacado: boolean | null; archivado: boolean | null };

export async function misEstados(miembroId: string): Promise<Map<string, EstadoCorreo>> {
  const items = await listAll<RawEstado>('estadosCorreo', { filter: f.eq('miembroId', miembroId) }, 5000);
  return new Map(items.map((e) => [e.hiloId, { id: e.id, hiloId: e.hiloId, leidoEn: e.leidoEn, destacado: !!e.destacado, archivado: !!e.archivado }]));
}

// Solo los estados de unos hilos (los de la entrada que se ven): lo que necesitan los contadores del menu y la campana.
// La lista completa de la persona (misEstados) puede pasar de 5000 filas y solo la pide la pantalla de Correos.
export async function estadosDeHilos(miembroId: string, hiloIds: string[]): Promise<Map<string, EstadoCorreo>> {
  const out = new Map<string, EstadoCorreo>();
  for (let i = 0; i < hiloIds.length; i += 80) {
    const tanda = hiloIds.slice(i, i + 80);
    const items = await listAll<RawEstado>('estadosCorreo', { filter: f.and(f.eq('miembroId', miembroId), f.in('hiloId', tanda)), depth: 0 }, tanda.length * 2);
    for (const e of items) out.set(e.hiloId, { id: e.id, hiloId: e.hiloId, leidoEn: e.leidoEn, destacado: !!e.destacado, archivado: !!e.archivado });
  }
  return out;
}

export type CambioEstado = Partial<Pick<EstadoCorreo, 'leidoEn' | 'destacado' | 'archivado'>>;

// Crea o actualiza el estado de un hilo para una persona.
export async function guardarEstado(miembroId: string, hiloId: string, actual: EstadoCorreo | undefined, cambios: CambioEstado) {
  if (actual) return update('estadosCorreo', actual.id, cambios);
  return create('estadosCorreo', { name: `${miembroId}:${hiloId}`, miembroId, hiloId, destacado: false, archivado: false, ...cambios });
}

/* ---------------- buzones de grupo ---------------- */

export type BuzonGrupo = { id: string; nombre: string; correo: string; cuentaId: string | null; miembros: string[]; firma: string; creaCasos: boolean; activo: boolean };
type RawGrupo = { id: string; name: string; correo: string | null; cuentaId: string | null; miembros: string[] | null; firma: string | null; creaCasos: boolean | null; activo: boolean | null };

export async function buzonesGrupo(): Promise<BuzonGrupo[]> {
  const items = await listAll<RawGrupo>('buzonesGrupo', { orderBy: 'name[AscNullsLast]' }, 200);
  return items.map((g) => ({ id: g.id, nombre: g.name, correo: g.correo ?? '', cuentaId: g.cuentaId, miembros: g.miembros ?? [], firma: g.firma ?? '', creaCasos: g.creaCasos !== false, activo: g.activo !== false }));
}

export type BuzonGrupoInput = Omit<BuzonGrupo, 'id'>;
const cuerpoGrupo = (g: BuzonGrupoInput) => ({ name: g.nombre.trim(), correo: g.correo.trim().toLowerCase(), cuentaId: g.cuentaId, miembros: g.miembros, firma: g.firma, creaCasos: g.creaCasos, activo: g.activo });
export const crearBuzonGrupo = (g: BuzonGrupoInput) => create('buzonesGrupo', cuerpoGrupo(g));
export const editarBuzonGrupo = (id: string, g: BuzonGrupoInput) => update('buzonesGrupo', id, cuerpoGrupo(g));
export const borrarBuzonGrupo = (id: string) => remove('buzonesGrupo', id);

/* ---------------- plantillas: textos rapidos y firmas ---------------- */

export type TipoPlantilla = 'TEXTO_RAPIDO' | 'FIRMA' | 'PLANTILLA';
export type Plantilla = { id: string; nombre: string; tipo: TipoPlantilla; asunto: string; cuerpo: string; buzon: string; duenoId: string | null };
type RawPlantilla = { id: string; name: string; tipo: TipoPlantilla | null; asunto: string | null; cuerpo: string | null; buzon: string | null; duenoId: string | null };

export async function plantillas(): Promise<Plantilla[]> {
  const items = await listAll<RawPlantilla>('plantillasCorreo', { orderBy: 'name[AscNullsLast]' }, 500);
  return items.map((p) => ({ id: p.id, nombre: p.name, tipo: p.tipo ?? 'TEXTO_RAPIDO', asunto: p.asunto ?? '', cuerpo: p.cuerpo ?? '', buzon: p.buzon ?? '', duenoId: p.duenoId }));
}
export type PlantillaInput = Omit<Plantilla, 'id'>;
const cuerpoPlantilla = (p: PlantillaInput) => ({ name: p.nombre.trim(), tipo: p.tipo, asunto: p.asunto, cuerpo: p.cuerpo, buzon: p.buzon.trim().toLowerCase(), duenoId: p.duenoId });
export const crearPlantilla = (p: PlantillaInput) => create('plantillasCorreo', cuerpoPlantilla(p));
export const editarPlantilla = (id: string, p: PlantillaInput) => update('plantillasCorreo', id, cuerpoPlantilla(p));
export const borrarPlantilla = (id: string) => remove('plantillasCorreo', id);
